import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export interface Ubicacion {
  id: string;
  nombre: string;
  direccion: string;
  ciudad: string;
  pais: string;
  barrio?: string;
  telefono?: string;
  email?: string;
  empresaId: string;
  activa: boolean;
  /** Espejo de empresas.direccion/ciudad/pais — se sincroniza por trigger, no se edita aquí. */
  esPrincipal: boolean;
  createdAt: Date;
  updatedAt: Date;
}

const mapRow = (r: any): Ubicacion => ({
  id: r.id,
  nombre: r.nombre || '',
  direccion: r.direccion || '',
  ciudad: r.ciudad || '',
  pais: r.pais || 'CO',
  barrio: r.barrio,
  telefono: r.telefono,
  email: r.email,
  empresaId: r.empresa_id,
  activa: r.activa !== false,
  esPrincipal: r.es_principal === true,
  createdAt: r.created_at ? new Date(r.created_at) : new Date(),
  updatedAt: r.updated_at ? new Date(r.updated_at) : new Date(),
});

interface UbicacionesState {
  ubicaciones: Ubicacion[];
  isLoading: boolean;
  error: string | null;
  fetchUbicaciones: (empresaId: string) => Promise<void>;
  createUbicacion: (empresaId: string, data: { nombre: string; direccion?: string; ciudad?: string; pais?: string }) => Promise<Ubicacion | null>;
  updateUbicacion: (id: string, data: Partial<{ nombre: string; direccion: string; ciudad: string; pais: string; activa: boolean }>) => Promise<void>;
  deleteUbicacion: (id: string) => Promise<void>;
}

export const useUbicacionesStore = create<UbicacionesState>((set, get) => ({
  ubicaciones: [],
  isLoading: false,
  error: null,

  fetchUbicaciones: async (empresaId: string) => {
    if (!empresaId) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('ubicaciones')
        .select('*')
        .eq('empresa_id', empresaId)
        .order('es_principal', { ascending: false })
        .order('nombre');
      if (error) throw error;
      set({ ubicaciones: (data || []).map(mapRow), isLoading: false });
    } catch (e: any) {
      set({ error: e?.message || 'Error al cargar ubicaciones', isLoading: false, ubicaciones: [] });
    }
  },

  createUbicacion: async (empresaId: string, data: { nombre: string; direccion?: string; ciudad?: string; pais?: string }) => {
    try {
      const { data: row, error } = await supabase
        .from('ubicaciones')
        .insert({
          empresa_id: empresaId,
          nombre: data.nombre,
          direccion: data.direccion || null,
          ciudad: data.ciudad || null,
          pais: data.pais || 'CO',
          activa: true,
        })
        .select()
        .single();
      if (error) throw error;
      const ubicacion = mapRow(row);
      set({ ubicaciones: [...get().ubicaciones, ubicacion] });
      return ubicacion;
    } catch (e: any) {
      set({ error: e?.message || 'Error al crear ubicación' });
      return null;
    }
  },

  updateUbicacion: async (id: string, updates: Partial<{ nombre: string; direccion: string; ciudad: string; pais: string; activa: boolean }>) => {
    try {
      const payload: any = {};
      if (updates.nombre != null) payload.nombre = updates.nombre;
      if (updates.direccion != null) payload.direccion = updates.direccion;
      if (updates.ciudad != null) payload.ciudad = updates.ciudad;
      if (updates.pais != null) payload.pais = updates.pais;
      if (updates.activa != null) payload.activa = updates.activa;
      const { error } = await supabase.from('ubicaciones').update(payload).eq('id', id);
      if (error) throw error;
      set({
        ubicaciones: get().ubicaciones.map((u) =>
          u.id === id ? { ...u, ...updates } : u
        ),
      });
    } catch (e: any) {
      set({ error: e?.message || 'Error al actualizar ubicación' });
      throw e;
    }
  },

  deleteUbicacion: async (id: string) => {
    try {
      const { error } = await supabase.from('ubicaciones').delete().eq('id', id);
      if (error) throw error;
      set({ ubicaciones: get().ubicaciones.filter((u) => u.id !== id) });
    } catch (e: any) {
      set({ error: e?.message || 'Error al eliminar ubicación' });
      throw e;
    }
  },
}));
